/** /patterns - inferred time-of-day / day-of-week behavior patterns,
 *  grouped by dimension. Dotted until confirmed. */
import { useCallback, useEffect, useState } from "react";
import { Clock, Check, X } from "lucide-react";
import { api } from "../lib/api";
import { TeachingBanner } from "../components/TeachingBanner";
import { Breadcrumb } from "../components/Breadcrumb";

type Pat = Record<string, unknown>;

const DIM_LABEL: Record<string, string> = {
  activity: "Activity",
  communication: "Communication",
  location: "Location",
  sleep: "Sleep",
  work: "Work",
  other: "Other",
};

export function Patterns() {
  const [items, setItems] = useState<Pat[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(() => {
    api.timePatternsList().then((d) => setItems(d.patterns || [])).catch(() => undefined);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const act = async (id: string, action: "confirm" | "reject") => {
    setBusy(id);
    try {
      await api.healthTimeAction("time-patterns", id, action);
      setItems((c) => c.filter((p) => p.id !== id || action === "confirm"));
      if (action === "confirm") refresh();
    } finally {
      setBusy(null);
    }
  };

  const byDim = items.reduce<Record<string, Pat[]>>((a, p) => {
    const d = String(p.dimension || "other");
    (a[d] ||= []).push(p);
    return a;
  }, {});

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-5">
      <Breadcrumb segments={[{ label: "Explore", href: "/explore" }, { label: "Library", href: "/explore/library" }, { label: "Patterns" }]} />
      <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">Patterns</h1>
      <TeachingBanner id="patterns_overview" />
      {Object.keys(byDim).length === 0 && (
        <p className="text-sm text-slate-500">No time patterns detected yet.</p>
      )}
      {Object.entries(byDim).map(([dim, list]) => (
        <section key={dim} className="space-y-2">
          <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500">
            {DIM_LABEL[dim] || dim}
          </h2>
          <ul className="space-y-2">
            {list.map((p) => {
              const id = String(p.id);
              const confirmed = Number(p.confirmed) === 1;
              const samples = Number(p.sample_count) || 1;
              return (
                <li
                  key={id}
                  className={`rounded-xl p-3 flex items-center justify-between gap-3 border ${
                    confirmed
                      ? "border-slate-200/70 dark:border-slate-700/50"
                      : "border-dashed border-slate-400 dark:border-slate-600"
                  } bg-white/60 dark:bg-slate-900/40`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-sky-500" />
                      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{String(p.label)}</span>
                      <span className="text-[11px] text-slate-400">{Math.round(Number(p.confidence) * 100)}%</span>
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                      {String(p.pattern_rule || "no rule")}
                      {samples > 1 ? ` · seen ${samples}x` : ""}
                    </div>
                  </div>
                  {!confirmed && (
                    <div className="flex gap-1.5 shrink-0">
                      <button type="button" disabled={busy === id} onClick={() => act(id, "confirm")} className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 text-white text-xs font-semibold px-2.5 py-1.5 disabled:opacity-50"><Check className="w-3.5 h-3.5" /></button>
                      <button type="button" disabled={busy === id} onClick={() => act(id, "reject")} className="inline-flex items-center gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold px-2.5 py-1.5 disabled:opacity-50"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
